import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import useSessionStore from '@/stores/useSessionStore'
import { brand } from '@/utils/brand'
import { getV5ApiUrl } from '@/utils/urlHelpers'

interface PaymentMethod {
  card_brand: string
  created_at: string
  display_name: string
  expiration_date: string
  favorite: boolean
  id: string
  last4: string
}

// Memoize the API function to avoid recreating it on every render.
function useCreatePaymentMethod(): UseMutationResult<PaymentMethod, Error, string> {
  const authToken = useSessionStore((state) => state.authToken)
  const queryClient = useQueryClient()

  const createPaymentMethod = useCallback(
    async (nonce: string): Promise<PaymentMethod> => {
      const url = getV5ApiUrl('/users/me/payments/methods/braintree')

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-brand-id': brand.key,
          'x-user-token': authToken,
        } as HeadersInit,
        body: JSON.stringify({ payment_method_nonce: nonce }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.errors || 'Failed to create payment method')
      }

      const data = await response.json()
      return data
    },
    [authToken],
  )

  // Return the memoized mutation hook
  return useMutation({
    mutationFn: createPaymentMethod,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['payment-methods', brand.key] }),
  })
}

export default useCreatePaymentMethod
